import { getApiUrl } from '../config/api';
import type { FlowAlertAiPatch, FlowAlertSettings } from '../utils/flowAlertSettingsStore';
import {
  applyFlowAlertAiPatch,
  loadFlowAlertSettings,
  saveFlowAlertSettings,
} from '../utils/flowAlertSettingsStore';

export interface AlertFieldCatalog {
  fields: Array<{
    key: string;
    label: string;
    type: string;
    source?: string;
    options?: string[];
  }>;
  by_source: Record<string, unknown[]>;
  operators: string[];
  computed_templates: Array<{
    id: string;
    label: string;
    type: string;
    expression?: string;
    description?: string;
  }>;
  sources: string[];
  questdb_tables: string[];
}

export interface AlertPreviewResult {
  source: string;
  filters?: Record<string, unknown>;
  sample_count: number;
  samples: Record<string, unknown>[];
  total?: number;
  error?: string;
}

let catalogCache: AlertFieldCatalog | null = null;

export async function fetchAlertCatalog(
  token: string,
): Promise<AlertFieldCatalog | null> {
  if (catalogCache) return catalogCache;
  try {
    const res = await fetch(getApiUrl('/api/ai/alerts/catalog'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (json.success && json.catalog) {
      catalogCache = json.catalog as AlertFieldCatalog;
      return catalogCache;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export async function fetchUserAlertProfile(
  token: string,
): Promise<FlowAlertSettings & { custom_fields?: CustomAlertFieldDef[] } | null> {
  try {
    const res = await fetch(getApiUrl('/api/ai/alerts/profile'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (json.success && json.profile) {
      return json.profile;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export interface CustomAlertFieldDef {
  id: string;
  label: string;
  type: 'number' | 'string' | 'boolean';
  expression?: string;
  description?: string;
  source?: string;
}

export type ExtendedFlowAlertSettings = FlowAlertSettings & {
  custom_fields?: CustomAlertFieldDef[];
  watchlist_symbols?: string[];
  snapshot_watchlists?: unknown[];
  default_source?: string;
  questdb_default_window_minutes?: number;
};

export async function saveUserAlertProfile(
  token: string,
  profile: ExtendedFlowAlertSettings,
): Promise<ExtendedFlowAlertSettings | null> {
  try {
    const res = await fetch(getApiUrl('/api/ai/alerts/profile'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ profile }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.profile ?? null;
  } catch {
    return null;
  }
}

/** Merge server profile into localStorage (AI chat + Flow tab share settings). */
export async function syncAlertProfileFromServer(token: string | null): Promise<void> {
  if (!token) return;
  const remote = await fetchUserAlertProfile(token);
  if (!remote) return;
  const local = loadFlowAlertSettings();
  const merged: ExtendedFlowAlertSettings = {
    ...local,
    ...remote,
    rules: Array.isArray(remote.rules) ? remote.rules : local.rules,
  };
  saveFlowAlertSettings(merged);
}

export async function persistAlertPatch(
  token: string | null,
  patch: FlowAlertAiPatch,
): Promise<ExtendedFlowAlertSettings> {
  const applied = applyFlowAlertAiPatch(patch);
  if (!token) return applied as ExtendedFlowAlertSettings;
  const saved = await saveUserAlertProfile(token, applied as ExtendedFlowAlertSettings);
  if (saved) {
    saveFlowAlertSettings(saved as FlowAlertSettings);
    return saved;
  }
  return applied as ExtendedFlowAlertSettings;
}

export async function previewAlertFilters(
  token: string,
  filters: Record<string, unknown>,
  limit = 10,
): Promise<AlertPreviewResult | null> {
  try {
    const res = await fetch(getApiUrl('/api/ai/alerts/preview'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filters, limit }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.preview ?? null;
  } catch {
    return null;
  }
}
